/**
 * Date Utilities for CliniKit
 * All dates are handled in Israel timezone (Asia/Jerusalem)
 * 
 * Key principles:
 * 1. Store dates in Firestore as ISO strings or native Date objects (converted to Timestamp)
 * 2. Always display dates in Israel timezone
 * 3. Use consistent date string format (YYYY-MM-DD) for comparisons
 */

import { format, parse, parseISO, startOfDay, endOfDay, startOfWeek, endOfWeek, addDays, addWeeks, addMonths, subDays, subWeeks, subMonths, getDay, getWeek, getWeekYear, getYear, isToday, isBefore, isAfter, differenceInMinutes } from 'date-fns';
import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';
import { Timestamp } from 'firebase/firestore';

// Israel timezone constant
export const ISRAEL_TIMEZONE = 'Asia/Jerusalem';

// ============================================
// CORE CONVERSION FUNCTIONS
// ============================================

/**
 * Convert any date input to a Date object in Israel timezone
 * Handles: Date objects, ISO strings, Firestore Timestamps
 */
export const toIsraelDate = (dateInput) => {
  const date = toInstant(dateInput);
  if (!date) return null;
  
  // Convert to Israel timezone
  return toZonedTime(date, ISRAEL_TIMEZONE);
};

/**
 * Parse any supported input (Timestamp / ISO string / Date / ms) into a plain
 * Date representing the real instant — NO timezone shifting.
 *
 * Use this (not toIsraelDate) as the input to formatInTimeZone: toIsraelDate
 * returns a wall-clock-shifted Date, so feeding it to formatInTimeZone applies
 * the Israel offset twice for users whose browser is outside Asia/Jerusalem.
 */
const toInstant = (dateInput) => {
  if (!dateInput) return null;
  
  let date;
  
  // Handle Firestore Timestamp
  if (dateInput instanceof Timestamp) {
    date = dateInput.toDate();
  }
  // Handle string (ISO format)
  else if (typeof dateInput === 'string') {
    date = parseISO(dateInput);
  }
  // Handle Date object
  else if (dateInput instanceof Date) {
    date = dateInput;
  }
  // Handle milliseconds timestamp
  else if (typeof dateInput === 'number') {
    date = new Date(dateInput);
  }
  else {
    return null;
  }
  
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Convert a Firestore Timestamp to a serializable ISO string
 * Use this when storing timestamps in Redux state
 */
export const timestampToISO = (timestamp) => {
  if (!timestamp) return null;
  
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate().toISOString();
  }
  
  if (timestamp instanceof Date) {
    return timestamp.toISOString();
  }
  
  if (typeof timestamp === 'string') {
    return timestamp; // Already a string
  }
  
  return null;
};

/**
 * Convert a date to Firestore Timestamp
 * Use this when saving to Firestore
 */
export const toFirestoreTimestamp = (dateInput) => {
  if (!dateInput) return null;
  
  if (dateInput instanceof Timestamp) {
    return dateInput;
  }
  
  let date;
  if (typeof dateInput === 'string') {
    date = parseISO(dateInput);
  } else if (dateInput instanceof Date) {
    date = dateInput;
  } else {
    return null;
  }
  
  return Timestamp.fromDate(date);
};

// ============================================
// DATE STRING FUNCTIONS (for comparisons)
// ============================================

/**
 * Get date string in YYYY-MM-DD format in Israel timezone
 * Use this for comparing dates (e.g., availability matching)
 */
export const getIsraelDateString = (dateInput) => {
  const date = toInstant(dateInput);
  if (!date) return null;
  
  return formatInTimeZone(date, ISRAEL_TIMEZONE, 'yyyy-MM-dd');
};

/**
 * Get date string from a Date object in local Israel format
 * Avoids timezone shifting issues with toISOString()
 */
export const formatLocalDateString = (date) => {
  if (!date) return null;
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

// ============================================
// FORMATTING FUNCTIONS (for display)
// ============================================

/**
 * Format date for display in Hebrew
 */
export const formatHebrewDate = (dateInput, formatStr = 'dd/MM/yyyy') => {
  const date = toInstant(dateInput);
  if (!date) return '';
  
  return formatInTimeZone(date, ISRAEL_TIMEZONE, formatStr);
};

/**
 * Format a calendar-only ISO string (YYYY-MM-DD) to DD-MM-YYYY for display.
 * Splits on components only so date-only values are not shifted by timezone.
 */
export const formatCalendarIsoToDdMmYyyy = (isoDate) => {
  if (isoDate == null || isoDate === '') return '';
  if (typeof isoDate !== 'string') return String(isoDate);
  const m = isoDate.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return isoDate;
  return `${m[3]}-${m[2]}-${m[1]}`;
};

/**
 * Format time for display (HH:mm)
 */
export const formatTime = (dateInput) => {
  const date = toInstant(dateInput);
  if (!date) return '';
  
  return formatInTimeZone(date, ISRAEL_TIMEZONE, 'HH:mm');
};

/**
 * Format date and time together
 */
export const formatDateTime = (dateInput) => {
  const date = toInstant(dateInput);
  if (!date) return '';
  
  return formatInTimeZone(date, ISRAEL_TIMEZONE, 'dd/MM/yyyy HH:mm');
};

/**
 * Format date in long Hebrew format
 */
export const formatLongHebrewDate = (dateInput) => {
  const date = toIsraelDate(dateInput);
  if (!date) return '';
  
  // Use toLocaleDateString for Hebrew formatting
  return date.toLocaleDateString('he-IL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

/**
 * Format date in short Hebrew format
 */
export const formatShortHebrewDate = (dateInput) => {
  const date = toIsraelDate(dateInput);
  if (!date) return '';
  
  return date.toLocaleDateString('he-IL', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
};

// ============================================
// WEEK FUNCTIONS (for availability)
// ============================================

/**
 * Get the ISO week ID for a date (e.g., "2026-W02")
 */
export const getWeekId = (dateInput = new Date()) => {
  const date = toIsraelDate(dateInput);
  if (!date) return null;
  
  // Week-year, not calendar year: the week spanning Dec 28 – Jan 3 must map to
  // ONE id from both ends (Sunday gave "2025-W01", Jan 1 gave "2026-W01" —
  // availability saved under one id was read by the other and then deleted as
  // "old"). Identical to the previous scheme for every non-boundary week.
  const week = getWeek(date, { weekStartsOn: 0 }); // Sunday start
  const year = getWeekYear(date, { weekStartsOn: 0 });
  
  return `${year}-W${String(week).padStart(2, '0')}`;
};

/**
 * Get the start of week (Sunday) in Israel timezone
 */
export const getWeekStart = (dateInput = new Date()) => {
  const date = toIsraelDate(dateInput);
  if (!date) return null;
  
  return startOfWeek(date, { weekStartsOn: 0 });
};

/**
 * Get the end of week (Saturday) in Israel timezone
 */
export const getWeekEnd = (dateInput = new Date()) => {
  const date = toIsraelDate(dateInput);
  if (!date) return null;
  
  return endOfWeek(date, { weekStartsOn: 0 });
};

/**
 * Get all days in a week starting from a given date
 */
export const getWeekDays = (startDate) => {
  const start = toIsraelDate(startDate);
  if (!start) return [];
  
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
};

// ============================================
// CALENDAR NAVIGATION FUNCTIONS
// ============================================

/**
 * Get today's date at start of day in Israel timezone
 */
export const getTodayStart = () => {
  return startOfDay(toZonedTime(new Date(), ISRAEL_TIMEZONE));
};

/**
 * Get today's date at end of day in Israel timezone
 */
export const getTodayEnd = () => {
  return endOfDay(toZonedTime(new Date(), ISRAEL_TIMEZONE));
};

/**
 * Navigate to previous period
 */
export const getPreviousPeriod = (currentDate, viewMode) => {
  const date = toIsraelDate(currentDate);
  if (!date) return new Date();
  
  switch (viewMode) {
    case 'day':
      return subDays(date, 1);
    case 'week':
      return subWeeks(date, 1);
    case 'month':
      return subMonths(date, 1);
    default:
      return date;
  }
};

/**
 * Navigate to next period
 */
export const getNextPeriod = (currentDate, viewMode) => {
  const date = toIsraelDate(currentDate);
  if (!date) return new Date();
  
  switch (viewMode) {
    case 'day':
      return addDays(date, 1);
    case 'week':
      return addWeeks(date, 1);
    case 'month':
      return addMonths(date, 1);
    default:
      return date;
  }
};

// ============================================
// COMPARISON FUNCTIONS
// ============================================

/**
 * Check if a date is today in Israel timezone
 */
export const isDateToday = (dateInput) => {
  const date = toIsraelDate(dateInput);
  if (!date) return false;
  
  const today = toZonedTime(new Date(), ISRAEL_TIMEZONE);
  return isToday(date);
};

/**
 * Check if a date is in the past (before today)
 */
export const isDatePast = (dateInput) => {
  const date = toIsraelDate(dateInput);
  if (!date) return false;
  
  const today = startOfDay(toZonedTime(new Date(), ISRAEL_TIMEZONE));
  return isBefore(date, today);
};

/**
 * Check if a date is in the future (after today)
 */
export const isDateFuture = (dateInput) => {
  const date = toIsraelDate(dateInput);
  if (!date) return false;
  
  const today = endOfDay(toZonedTime(new Date(), ISRAEL_TIMEZONE));
  return isAfter(date, today);
};

/**
 * Get the duration in minutes between two dates
 */
export const getDurationMinutes = (startDate, endDate) => {
  const start = toIsraelDate(startDate);
  const end = toIsraelDate(endDate);
  
  if (!start || !end) return 0;
  
  return differenceInMinutes(end, start);
};

// ============================================
// APPOINTMENT HELPERS
// ============================================

/**
 * Create appointment start and end times
 */
export const createAppointmentTimes = (date, startHour, startMinute, durationMinutes) => {
  const baseDate = toIsraelDate(date);
  if (!baseDate) return null;
  
  const startTime = new Date(baseDate);
  startTime.setHours(startHour, startMinute, 0, 0);
  
  const endTime = new Date(startTime);
  endTime.setMinutes(endTime.getMinutes() + durationMinutes);
  
  return {
    startTime: startTime.toISOString(),
    endTime: endTime.toISOString(),
  };
};

/**
 * Parse time string (HH:mm) to hours and minutes
 */
export const parseTimeString = (timeStr) => {
  if (!timeStr) return { hours: 0, minutes: 0 };
  
  const [hours, minutes] = timeStr.split(':').map(Number);
  return { hours: hours || 0, minutes: minutes || 0 };
};

/**
 * Create time string from hours and minutes
 */
export const createTimeString = (hours, minutes = 0) => {
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

// ============================================
// FIRESTORE DATA SERIALIZATION
// ============================================

/**
 * Serialize an object for Redux state (convert Timestamps to ISO strings)
 * Use this when fetching data from Firestore to store in Redux
 */
export const serializeForRedux = (data) => {
  // Only short-circuit for null/undefined. Using `!data` here would convert
  // falsy primitives (false, 0, "") into null and destroy booleans on read
  // (e.g. `isActive: false` became `isActive: null`, breaking equality checks).
  if (data === null || data === undefined) return null;

  if (Array.isArray(data)) {
    return data.map(item => serializeForRedux(item));
  }

  if (data instanceof Timestamp) {
    return data.toDate().toISOString();
  }

  if (data instanceof Date) {
    return data.toISOString();
  }

  if (typeof data === 'object') {
    const serialized = {};
    for (const [key, value] of Object.entries(data)) {
      serialized[key] = serializeForRedux(value);
    }
    return serialized;
  }

  return data;
};

/**
 * Prepare data for Firestore (convert ISO strings to Timestamps for date fields)
 * Pass in the field names that should be converted to Timestamps
 */
export const prepareForFirestore = (data, dateFields = []) => {
  if (!data) return null;
  
  const prepared = { ...data };
  
  for (const field of dateFields) {
    if (prepared[field]) {
      if (typeof prepared[field] === 'string') {
        prepared[field] = Timestamp.fromDate(parseISO(prepared[field]));
      } else if (prepared[field] instanceof Date) {
        prepared[field] = Timestamp.fromDate(prepared[field]);
      }
    }
  }
  
  return prepared;
};

// ============================================
// HEBREW DAY NAMES
// ============================================

export const HEBREW_DAYS_SHORT = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];
export const HEBREW_DAYS_FULL = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
export const HEBREW_DAYS_WITH_APOSTROPHE = ["א'", "ב'", "ג'", "ד'", "ה'", "ו'", "ש'"];

export const getHebrewDayName = (dateInput, format = 'short') => {
  const date = toIsraelDate(dateInput);
  if (!date) return '';
  
  const dayIndex = getDay(date);
  
  switch (format) {
    case 'full':
      return HEBREW_DAYS_FULL[dayIndex];
    case 'apostrophe':
      return HEBREW_DAYS_WITH_APOSTROPHE[dayIndex];
    default:
      return HEBREW_DAYS_SHORT[dayIndex];
  }
};

// ============================================
// UNIQUE ID GENERATION
// ============================================

/**
 * Generate a unique appointment ID (10-20 characters)
 * Format: [timestamp-base36]-[random-chars]
 * Example: k8n5p2q-x7m4w9
 */
export const generateUniqueAppointmentId = () => {
  // Get timestamp in base36 for compact representation
  const timestamp = Date.now().toString(36);
  
  // Generate random alphanumeric string (6 characters)
  const randomChars = Array.from({ length: 6 }, () => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    return chars[Math.floor(Math.random() * chars.length)];
  }).join('');
  
  // Combine with hyphen for readability
  return `${timestamp}-${randomChars}`;
};

export default {
  ISRAEL_TIMEZONE,
  toIsraelDate,
  timestampToISO,
  toFirestoreTimestamp,
  getIsraelDateString,
  formatLocalDateString,
  formatHebrewDate,
  formatCalendarIsoToDdMmYyyy,
  formatTime,
  formatDateTime,
  formatLongHebrewDate,
  formatShortHebrewDate,
  getWeekId,
  getWeekStart,
  getWeekEnd,
  getWeekDays,
  getTodayStart,
  getTodayEnd,
  getPreviousPeriod,
  getNextPeriod,
  isDateToday,
  isDatePast,
  isDateFuture,
  getDurationMinutes,
  createAppointmentTimes,
  parseTimeString,
  createTimeString,
  serializeForRedux,
  prepareForFirestore,
  generateUniqueAppointmentId,
  HEBREW_DAYS_SHORT,
  HEBREW_DAYS_FULL,
  HEBREW_DAYS_WITH_APOSTROPHE,
  getHebrewDayName,
};

